import { z } from 'zod';
import { FormSectionConfig } from '../constants';

const formSection = FormSectionConfig.typeOfHelp.id;

export enum FieldEnum {
  assistanceRequestCode = 'assistanceRequestCode',
  notes = 'notes',
  consentToShareReferralWithMicrosoftSellers = 'consentToShareReferralWithMicrosoftSellers',
}

export const typeOfHelpDefaultValues = {
  [FieldEnum.assistanceRequestCode]: 'GeneralOrOther',
  [FieldEnum.consentToShareReferralWithMicrosoftSellers]: 'yes',
  [FieldEnum.notes]: '',
};

export const fieldConfig = {
  [FieldEnum.assistanceRequestCode]: {
    name: `${formSection}.${FieldEnum.assistanceRequestCode}`,
    label: 'Select type of help from Microsoft',
    required: true,
    type: 'single-select',
  },
  [FieldEnum.notes]: {
    name: `${formSection}.${FieldEnum.notes}`,
    label: 'Notes to Microsoft',
    placeholder:
      'Add information you want to share about the deal with Microsoft, and avoid sharing personally identifiable data.',
    type: 'textarea',
    required: true,
  },
  [FieldEnum.consentToShareReferralWithMicrosoftSellers]: {
    name: `${formSection}.${FieldEnum.consentToShareReferralWithMicrosoftSellers}`,
    label: 'Would you like Microsoft sellers to view this deal?',
    type: 'radio',
    required: true,
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
} as const;

/** Type of Help schema ---------------- */
const BaseTypeOfHelpInput = z.object({
  [FieldEnum.assistanceRequestCode]: z.enum([
    'GeneralOrOther',
    'CustomerTechnicalArchitecture',
    'ProofOfConceptOrDemo',
    'QuotesOrLicensing',
    'PostSalesCustomerSuccess',
    'WorkloadSpecificValueProposition',
    'NoHelpNeededFromMicrosoft',
  ]),
});

const HelpNeededInput = BaseTypeOfHelpInput.extend({
  [FieldEnum.notes]: z.string().min(1, 'Required'), // Notes is required for help needed cases
});

const NoHelpNeededInput = BaseTypeOfHelpInput.extend({
  [FieldEnum.consentToShareReferralWithMicrosoftSellers]: z
    .enum(['yes', 'no'])
    .default('yes'),
});

export const TypeOfHelpSchema = z.discriminatedUnion('assistanceRequestCode', [
  // All cases except NoHelpNeededFromMicrosoft
  HelpNeededInput.extend({
    [FieldEnum.assistanceRequestCode]: z.enum([
      'GeneralOrOther',
      'CustomerTechnicalArchitecture',
      'ProofOfConceptOrDemo',
      'QuotesOrLicensing',
      'PostSalesCustomerSuccess',
      'WorkloadSpecificValueProposition',
    ]),
  }),
  // NoHelpNeededFromMicrosoft case
  NoHelpNeededInput.extend({
    [FieldEnum.assistanceRequestCode]: z.literal('NoHelpNeededFromMicrosoft'),
  }),
]);

export type TypeOfHelpInput = z.infer<typeof TypeOfHelpSchema>;
